<script setup lang="ts">
import type { Album, AlbumItem } from '~/types/blog'
import BlogAlbumLayoutSwitcher from '~/components/blog/AlbumLayoutSwitcher.vue'
import { normalizeFieldText } from '~/composables/useCustomFields'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { isAppleSafari, isTouchOnlyInput, resolveAlbumLightboxURL, resolveAlbumMediaSources, selectAlbumMediaSources } from '~/utils/albumMedia.mjs'
import { shouldShowAlbumSkeleton } from '~/utils/albumLoading.mjs'
import { albumDetailPlaceholderHeights } from '~/utils/masonry.mjs'
const route = useRoute()
const blogApi = useBlogApi()
const { isStaleNavigation } = useNavigationStaleGuard()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)
const createStaleAlbumFallback = (): Album => ({
  ID: 0,
  title: '',
  slug: ''
})

const slug = String(route.params.slug || '').trim()
const { data: album, pending } = await useAsyncData<Album>(`album:${slug}`, async (_nuxtApp, { signal }) => {
  try {
    const result = await blogApi.getAlbumBySlug(slug, { signal })
    if (!result?.ID && !isStaleNavigation()) {
      throw createError({ status: 404, message: '相册不存在' })
    }
    return result
  } catch (error) {
    if (isStaleNavigation() || isAbortLikeError(error)) {
      return createStaleAlbumFallback()
    }
    throw error
  }
}, { lazy: true })

if (!pending.value && !album.value?.ID && !isStaleNavigation()) {
  throw createError({ status: 404, message: '相册不存在' })
}

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName')
const pageDesc = computed(() => album.value?.description || normalizeFieldText(siteData?.setting?.customFields, 'albumsPageDescription', '浏览公开相册内容。'))
const albumLocation = computed(() => normalizeFieldText(album.value?.customFields, 'location', ''))
const albumPublishedAt = computed(() => {
  const value = album.value?.publishedAt
  if (!value) {
    return '未发布'
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN')
})
const seoTitle = computed(() => `${album.value?.title || '相册'} - ${siteName}`)
const seoOgImage = album.value?.cover ? resolveImageAssetUrl(album.value.cover) : undefined

useSeoMeta({
  title: () => seoTitle.value,
  description: () => pageDesc.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => pageDesc.value,
  ogImage: seoOgImage
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})

const allItems = computed(() => album.value?.items || [])
const { layout: albumLayout, columns: albumLayoutColumns, batchSize } = useAlbumLayout('detail')
const hydrated = ref(false)
const { items, sourceBatch, batchStart, imagesSettled, hasMore, loadMore, markImageLoaded, markImageFailed, isImageFailed, isBatchLoading } = useAlbumImageBatches<AlbumItem>(allItems, batchSize, hydrated, item => {
  const sources = resolveAlbumMediaSources(item)
  return Boolean(sources.displayURL || sources.fallbackURL)
})
const albumImageCount = computed(() => allItems.value.length)
const sentinel = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null

// bindObserver 接近末尾时加载下一批，批次完成后重新观察位置
const bindObserver = () => {
  if (!import.meta.client) return
  observer?.disconnect()
  observer = null
  if (!sentinel.value || !hasMore.value || !imagesSettled.value) return
  observer = new IntersectionObserver(entries => {
    if (entries[0]?.isIntersecting) loadMore()
  }, { rootMargin: '260px 0px' })
  observer.observe(sentinel.value)
}

watch([sentinel, hasMore, imagesSettled], bindObserver, { flush: 'post' })
onMounted(bindObserver)
onBeforeUnmount(() => observer?.disconnect())
const preferOriginalHDR = ref(false)
const touchOnlyInput = ref(false)
const albumSources = computed(() =>
  items.value
    .map((item, itemIndex) => {
      if (isImageFailed(item)) return null
      const src = resolveImageAssetUrl(resolveAlbumLightboxURL(item, preferOriginalHDR.value))
      if (!src) {
        return null
      }
      const description = (item.description || '').trim()
      const itemTitle = (item.title || '').trim()
      const albumTitle = (album.value?.title || '').trim()
      const caption = description || itemTitle || albumTitle || '图片'
      return {
        itemIndex,
        src,
        fallbackSrc: resolveImageAssetUrl(selectAlbumMediaSources(resolveAlbumMediaSources(item), preferOriginalHDR.value).fallbackURL),
        title: caption,
        alt: itemTitle || description || albumTitle || '图片'
      }
    })
    .filter((item): item is { itemIndex: number; src: string; fallbackSrc: string; title: string; alt: string } => Boolean(item))
)
const liked = ref(false)
const likeEffectToken = ref(0)
const showInfoSkeleton = computed(() => pending.value || !hydrated.value)
const showImagesSkeleton = computed(() => shouldShowAlbumSkeleton(pending.value, hydrated.value, batchStart.value > 0 || imagesSettled.value))
const albumLike = useOptimisticLike('album', blogApi.likeAlbum)
const {
  visible: albumLightboxVisible,
  sources: albumLightboxSources,
  index: albumLightboxIndex,
  open: openAlbumLightboxImages,
  close: closeAlbumLightbox
} = useImageLightbox()

const syncLikedState = () => {
  liked.value = albumLike.isLiked(album.value)
}

const likeAlbum = () => {
  albumLike.like(album.value, () => {
    liked.value = true
    likeEffectToken.value += 1
  })
}

onMounted(() => {
  hydrated.value = true
  syncLikedState()
  preferOriginalHDR.value = isAppleSafari(navigator.userAgent, navigator.vendor)
  touchOnlyInput.value = isTouchOnlyInput(window.matchMedia.bind(window))
})

watch(
  () => album.value?.ID,
  () => syncLikedState()
)

// openAlbumLightbox 按卡片索引定位可用图片，跳过加载失败的卡片
const openAlbumLightbox = (itemIndex: number) => {
  if (touchOnlyInput.value || isBatchLoading(items.value[itemIndex])) {
    return
  }
  const sourceIndex = albumSources.value.findIndex(source => source.itemIndex === itemIndex)
  if (sourceIndex < 0) return
  openAlbumLightboxImages(albumSources.value, sourceIndex)
}

const preventTouchOnlyNativeEvent = (event: Event) => {
  if (touchOnlyInput.value) {
    event.preventDefault()
  }
}
</script>

<template>
  <section class="album-detail-page">
    <BlogAlbumSkeleton
      v-if="showInfoSkeleton"
      variant="info"
      :columns="albumLayoutColumns"
    />

    <div v-if="album?.ID" v-show="!showInfoSkeleton" class="card album-head">
      <NuxtLink to="/albums" class="back-link"><i class="fa-solid fa-arrow-left" aria-hidden="true" />返回相册</NuxtLink>
      <h1>{{ album?.title }}</h1>
      <div class="album-meta">
        <span><i class="fa-regular fa-clock" aria-hidden="true" />{{ albumPublishedAt }}</span>
        <span><i class="fa-regular fa-image" aria-hidden="true" />{{ albumImageCount }} 张图片</span>
        <span v-if="albumLocation"><i class="fa-solid fa-location-dot" aria-hidden="true" />{{ albumLocation }}</span>
      </div>
      <p>{{ album?.description || '暂无描述' }}</p>
      <div class="album-actions">
        <BlogLikeButton
          variant="count"
          :liked="liked"
          :count="album?.likeCount || 0"
          :effect-token="likeEffectToken"
          @click="likeAlbum"
        />
        <BlogAlbumLayoutSwitcher
          v-model="albumLayout"
          :disabled="showInfoSkeleton || !imagesSettled"
        />
      </div>
    </div>

    <BlogAlbumSkeleton
      v-if="showImagesSkeleton"
      variant="detail"
      :columns="albumLayoutColumns"
      :count="items.length || batchSize"
    />

    <!-- @vue-generic {AlbumItem} -->
    <BlogMasonry
      v-if="items.length"
      v-show="!showImagesSkeleton"
      :key="sourceBatch"
      :items="items"
      :columns="albumLayoutColumns"
      :tablet-columns="albumLayoutColumns"
      :estimated-heights="albumDetailPlaceholderHeights"
      class="gallery-grid"
    >
      <template #default="{ item, index }">
        <article
          class="thumb"
          :role="touchOnlyInput ? undefined : 'button'"
          :tabindex="touchOnlyInput ? undefined : 0"
          @click="openAlbumLightbox(index)"
          @keydown.enter.prevent="openAlbumLightbox(index)"
          @keydown.space.prevent="openAlbumLightbox(index)"
          @contextmenu="preventTouchOnlyNativeEvent"
          @selectstart="preventTouchOnlyNativeEvent"
          @dragstart="preventTouchOnlyNativeEvent"
        >
          <BlogAlbumSkeleton
            v-if="isBatchLoading(item)"
            variant="detail"
            :columns="1"
            :count="1"
            :start-index="index"
          />
          <ClientOnly>
            <BlogAlbumMedia
              v-show="!isBatchLoading(item)"
              :item="item"
              :alt="item.title || album?.title || '图片'"
              @load="markImageLoaded(item)"
              @error="markImageFailed(item)"
            >
              <BlogAlbumItemOverlay :description="item.description" :hdr="item.isHDR" />
            </BlogAlbumMedia>
            <template #fallback>
              <div
                class="album-media album-media-fallback"
                role="img"
                :aria-label="item.title || album?.title || '图片'"
              />
              <BlogAlbumItemOverlay :description="item.description" :hdr="item.isHDR" />
            </template>
          </ClientOnly>
        </article>
      </template>
    </BlogMasonry>
    <div v-else-if="!showImagesSkeleton" class="empty-card">该相册暂无图片</div>
    <div v-if="hasMore && !showImagesSkeleton" ref="sentinel" class="scroll-sentinel" />
    <BlogEasyLightbox
      :visible="albumLightboxVisible"
      :sources="albumLightboxSources"
      :index="albumLightboxIndex"
      @hide="closeAlbumLightbox"
    />
  </section>
</template>

<style scoped>
.album-detail-page {
  display: grid;
  gap: 14px;
}

.album-head {
  padding: 20px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  color: var(--text-soft);
  text-decoration: none;
}

h1 {
  font-size: var(--heading-title-size);
}

.album-head p {
  margin-top: 8px;
  color: var(--text-soft);
}

.album-meta {
  margin-top: 10px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  color: var(--text-soft);
  font-size: 13px;
}

.album-meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.album-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.gallery-grid {
  width: 100%;
}

.scroll-sentinel {
  width: 100%;
  height: 1px;
}

.thumb {
  position: relative;
  display: block;
  width: 100%;
  margin-bottom: 0;
  padding: 0;
  border-radius: 12px;
  overflow: hidden;
  text-align: left;
  color: inherit;
  cursor: pointer;
  background: var(--bg);
  break-inside: avoid;
}

.album-media-fallback {
  position: relative;
  width: 100%;
  min-height: var(--album-placeholder-height);
  overflow: hidden;
  background: var(--album-skeleton-background);
}

@media (hover: none) and (pointer: coarse) {
  .thumb {
    cursor: default;
  }

  .thumb,
  .thumb :deep(*) {
    -webkit-touch-callout: none;
    user-select: none;
  }
}
</style>
